import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, Inbox, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { PartnerShell } from "./Dashboard";

const BAND_TONE = {
  excellent: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Excellent" },
  good:      { bg: "bg-green-100",   text: "text-green-800",   label: "Good" },
  caution:   { bg: "bg-amber-100",   text: "text-amber-800",   label: "Caution" },
  action:    { bg: "bg-rose-100",    text: "text-rose-800",    label: "Action" },
  critical:  { bg: "bg-rose-200",    text: "text-rose-900",    label: "Critical" },
  unsafe:    { bg: "bg-rose-100",    text: "text-rose-900",    label: "Unsafe" },
};
const SOURCE_LABEL = {
  partner_link:               { bg: "bg-emerald-100", text: "text-emerald-800", label: "Partner link" },
  stored_partner_attribution: { bg: "bg-amber-100",   text: "text-amber-800",   label: "Stored attribution" },
  region_allocation:          { bg: "bg-indigo-100",  text: "text-indigo-800",  label: "Region allocated" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); } catch { return "—"; }
}

export default function PartnerPassports() {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/partner/passports")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Unable to load passports"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <PartnerShell active="passports">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 mb-1">Water Passports</div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="partner-passports-heading">
          Customer Water Passports
        </h1>
        <div className="text-sm text-slate-600 mt-1" data-testid="partner-passports-total">
          {loading ? "Loading…" : `${data.total} passport${data.total === 1 ? "" : "s"} linked to your franchisee`}
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-6 text-rose-800 flex items-start gap-3" data-testid="partner-passports-error">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {!error && !loading && data.rows.length === 0 && (
        <div className="rounded-xl bg-white border border-slate-200 p-10 text-center" data-testid="partner-passports-empty">
          <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <div className="text-sm font-semibold text-slate-700">No passports yet</div>
          <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Water Passports for customers attributed to your franchisee will appear here once their first test result is published by the lab.
          </div>
        </div>
      )}

      {!error && data.rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="partner-passports-grid">
          {data.rows.map((row) => {
            const p = row.property;
            const t = row.latest_test;
            const tone = BAND_TONE[(t?.band || "").toLowerCase()] || BAND_TONE.good;
            const src = SOURCE_LABEL[t?.booking_source];
            return (
              <Link
                to={`/partner/passports/${p.id}`}
                key={p.id}
                data-testid={`partner-passport-card-${p.id}`}
                className="rounded-xl bg-white border border-slate-200 p-5 hover:shadow-md hover:border-cyan-200 transition-all group"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                      {p.property_type || "Property"}
                    </div>
                    <div className="font-bold text-slate-900 text-base truncate" data-testid={`partner-passport-card-${p.id}-address`}>
                      {p.nickname || p.address}
                    </div>
                    {p.nickname && (
                      <div className="text-xs text-slate-500 truncate mt-0.5">{p.address}</div>
                    )}
                    <div className="text-xs text-slate-500 mt-0.5" data-testid={`partner-passport-card-${p.id}-owner`}>
                      {p.owner_email || "—"}
                    </div>
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${tone.bg} ${tone.text} flex flex-col items-center justify-center shrink-0`}>
                    <ShieldCheck className="w-4 h-4" />
                    <div className="text-[10px] font-bold mt-0.5" data-testid={`partner-passport-card-${p.id}-score`}>
                      {t?.health_score ?? "—"}
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Latest test</span>
                    <span className="font-semibold text-slate-700" data-testid={`partner-passport-card-${p.id}-test-date`}>
                      {fmtDate(t?.collected_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Test package</span>
                    <span className="text-slate-700 truncate ml-2">{t?.test_name || "—"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Status</span>
                    <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${tone.bg} ${tone.text}`} data-testid={`partner-passport-card-${p.id}-band`}>
                      {tone.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Retest due</span>
                    <span className="font-semibold text-slate-700" data-testid={`partner-passport-card-${p.id}-retest`}>
                      {fmtDate(row.next_retest?.next_due_at)}
                    </span>
                  </div>
                  {src && (
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-slate-500">Attribution</span>
                      <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider rounded px-1.5 py-0.5 ${src.bg} ${src.text}`}>
                        {src.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] text-slate-500" data-testid={`partner-passport-card-${p.id}-test-count`}>
                    {row.test_count} test{row.test_count === 1 ? "" : "s"} on record
                  </span>
                  <span className="text-xs font-semibold text-cyan-700 group-hover:text-cyan-800 inline-flex items-center gap-1">
                    View passport <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </PartnerShell>
  );
}
